import _ from "lodash";
import { type UnknownAction } from "@reduxjs/toolkit";
import {
  CallMeeting,
  FetchCallMettingRequest,
  FetchCallMettingResponse,
  FetchCallMettingErrorResponse,
  FetchCallMettingsErrorResponse,
  FetchCallMettingsRequest,
  FetchCallMettingsResponse,
  UploadCallRecordingRequest,
  UploadCallRecordingResponse,
  UploadCallRecordingErrorResponse,
  DeleteUploadCallRecordingRequest,
  DeleteUploadCallRecordingResponse,
  DeleteUploadCallRecordingErrorResponse,
  FetchUploadCallMeetingsResponse,
  FetchUploadCallMeetingsErrorResponse,
} from "src/types/coaching/callLibrary";
import {
  FETCH_CALL_MEETING_REQUEST,
  FETCHING_CALL_MEETING_SUCCEEDED,
  FETCHING_CALL_MEETING_FAILED,
  FETCH_CALL_MEETINGS_REQUEST,
  FETCHING_CALL_MEETINGS_FAILED,
  FETCHING_CALL_MEETINGS_SUCCEEDED,
  INVALIDATE_CALL_MEETING,
  UPLOAD_CALL_RECORDING_REQUEST,
  UPLOAD_CALL_RECORDING_SUCCEEDED,
  UPLOAD_CALL_RECORDING_FAILED,
  DELETE_UPLOAD_CALL_RECORDING_REQUEST,
  DELETING_UPLOAD_CALL_RECORDING_SUCCEEDED,
  DELETING_UPLOAD_CALL_RECORDING_FAILED,
  WS_UPLOAD_CALL_RECORDING_TRANSCRIPT_GENERATION_COMPLETED,
  WS_UPLOAD_CALL_RECORDING_EVALUATION_COMPLETED,
  INVALIDATE_UPLOAD_CALL_RECORDING,
  FETCH_UPLOAD_CALL_MEETINGS_REQUEST,
  FETCHING_UPLOAD_CALL_MEETINGS_SUCCEEDED,
  FETCHING_UPLOAD_CALL_MEETINGS_FAILED,
} from "src/redux/actions/actionTypes";
interface CallMeetingsState {
  loading: boolean;
  loaded: boolean;
  items: CallMeeting[];
  meeting: {
    [key: string]: CallMeetingState;
  };
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
  error: any | null;
  upload: {
    [key: string]: UploadCall;
  };
  uploadedItems: UploadedCallsState;
  deletion: {
    [key: string]: CallRecordingDeletionState;
  };
}

interface UploadCall {
  uploading: boolean;
  uploaded: boolean;
}

interface UploadedCallsState {
  loading: boolean;
  loaded: boolean;
  items: UploadedCallItem[];
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
}

export interface UploadedCallItem {
  recordingId: string;
  response: CallMeeting | null;
  transcriptProcessing?: {
    status: UploadCallProcessingStatus;
    error?: any | null;
  };
  evaluationProcessing?: {
    status: UploadCallProcessingStatus;
    error?: any | null;
  };
  error?: any;
}

interface CallMeetingState {
  loading: boolean;
  loaded: boolean;
  syncing?: boolean;
  synced?: boolean;
  item: CallMeeting | null;
  error: any;
}

interface CallRecordingDeletionState {
  id: string;
  deleting: boolean;
  deleted: boolean;
  error: any;
}

export type UploadCallProcessingStatus = "pending" | "in_progress" | "completed" | "failed";

const initialState: CallMeetingsState = {
  loading: false,
  loaded: false,
  items: [],
  meeting: {},
  pagination: {
    currentPage: 0,
    totalPages: 0,
    recordsPerPage: 0,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
  error: null,
  upload: {},
  uploadedItems: {
    loading: false,
    loaded: false,
    items: [],
    pagination: {
      currentPage: 0,
      totalPages: 0,
      recordsPerPage: 0,
      totalRecordsCount: 0,
      hasMoreItems: false,
    },
  },
  deletion: {},
};

export function callMeetingReducer(
  state: CallMeetingsState = initialState,
  action: UnknownAction,
): CallMeetingsState {
  switch (action.type) {
    case FETCH_CALL_MEETINGS_REQUEST: {
      const { page } = action.payload as FetchCallMettingsRequest;

      let existingItems: any[] = state.items || [];
      if (page === 0) {
        existingItems = [];
      }

      return {
        ...state,
        items: existingItems,
        loading: true,
        loaded: false,
        error: null,
      };
    }

    case FETCHING_CALL_MEETINGS_SUCCEEDED: {
      const { response } = action.payload as FetchCallMettingsResponse;

      if (!response) {
        return state;
      }

      const existingItems: any[] = state.items || [];
      let records = response.records;

      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      return {
        ...state,
        loading: false,
        loaded: true,
        items: records,
        pagination: {
          currentPage: response.currentPage,
          totalPages: response.totalPages,
          recordsPerPage: response.recordsPerPage,
          totalRecordsCount: response.totalRecordsCount,
          hasMoreItems: response.currentPage < response.totalPages - 1,
        },
        error: null,
      };
    }

    case FETCHING_CALL_MEETINGS_FAILED: {
      const { error } = action.payload as FetchCallMettingsErrorResponse;

      return {
        ...state,
        loading: false,
        loaded: false,
        error: error,
        items: [],
      };
    }

    case FETCH_CALL_MEETING_REQUEST: {
      const { id } = action.payload as FetchCallMettingRequest;

      const meetingState = state.meeting[id] || {
        loading: false,
        loaded: false,
        item: null,
        error: null,
      };

      return {
        ...state,
        meeting: {
          ...state.meeting,
          [id]: {
            ...meetingState,
            loading: true,
            loaded: false,
            error: null,
          },
        },
      };
    }

    case FETCHING_CALL_MEETING_SUCCEEDED: {
      const { response } = action.payload as FetchCallMettingResponse;
      const id = response.id;

      const meetingState = state.meeting[id] || {
        loading: false,
        loaded: false,
        item: null,
        error: null,
      };

      return {
        ...state,
        meeting: {
          ...state.meeting,
          [id]: {
            ...meetingState,
            loading: false,
            loaded: true,
            item: response,
            error: null,
          },
        },
      };
    }

    case FETCHING_CALL_MEETING_FAILED: {
      const { id, error } = action.payload as FetchCallMettingErrorResponse;

      const meetingState = state.meeting[id] || {
        loading: false,
        loaded: false,
        item: null,
        error: null,
      };

      return {
        ...state,
        meeting: {
          ...state.meeting,
          [id]: {
            ...meetingState,
            loading: false,
            loaded: false,
            item: null,
            error: error,
          },
        },
      };
    }

    case INVALIDATE_CALL_MEETING: {
      return {
        ...state,
        meeting: {},
      };
    }

    case UPLOAD_CALL_RECORDING_REQUEST: {
      const { id } = action.payload as UploadCallRecordingRequest;

      return {
        ...state,
        upload: {
          ...state.upload,
          [id]: {
            uploading: true,
            uploaded: false,
          },
        },
      };
    }

    case UPLOAD_CALL_RECORDING_SUCCEEDED: {
      const { id, response } = action.payload as UploadCallRecordingResponse;
      if (!response) return state;

      const newItem: UploadedCallItem = {
        recordingId: response.id,
        response: response,
        error: null,
      };

      return {
        ...state,
        upload: {},
        uploadedItems: {
          ...state.uploadedItems,
          items: [newItem, ...state.uploadedItems.items],
        },
      };
    }

    case UPLOAD_CALL_RECORDING_FAILED: {
      const { id } = action.payload as UploadCallRecordingErrorResponse;
    
      const updatedUpload = { ...state.upload };
      delete updatedUpload[id];
    
      return {
        ...state,
        upload: updatedUpload,
      };
    }

    case WS_UPLOAD_CALL_RECORDING_TRANSCRIPT_GENERATION_COMPLETED: {
      const { recordingId } = action.payload as any;

      const updatedItems = state.uploadedItems.items.map((item) => {
        if (item.recordingId !== recordingId) return item;
        return {
          ...item,
          transcriptProcessing: {
            status: "completed" as UploadCallProcessingStatus,
            error: null,
          },
        };
      });

      return {
        ...state,
        uploadedItems: {
          ...state.uploadedItems,
          items: updatedItems,
        },
      };
    }

    case WS_UPLOAD_CALL_RECORDING_EVALUATION_COMPLETED: {
      const { recordingId } = action.payload as any;

      const updatedItems = state.uploadedItems.items.map((item) => {
        if (item.recordingId !== recordingId) return item;
        return {
          ...item,
          evaluationProcessing: {
            status: "completed" as UploadCallProcessingStatus,
            error: null,
          },
        };
      });

      return {
        ...state,
        uploadedItems: {
          ...state.uploadedItems,
          items: updatedItems,
        },
      };
    }

    case DELETE_UPLOAD_CALL_RECORDING_REQUEST: {
      const { id } = action.payload as DeleteUploadCallRecordingRequest;

      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: {
            id,
            deleting: true,
            deleted: false,
            error: null,
          },
        },
      };
    }

    case DELETING_UPLOAD_CALL_RECORDING_SUCCEEDED: {
      const { id } = action.payload as DeleteUploadCallRecordingResponse;

      return {
        ...state,
        uploadedItems: {
          ...state.uploadedItems,
          items: state.uploadedItems.items.filter((item) => item.recordingId !== id),
        },
        items: state.items.filter((item) => item.id !== id),
        deletion: {
          ...state.deletion,
          [id]: {
            id,
            deleting: false,
            deleted: true,
            error: null,
          },
        },
      };
    }

    case DELETING_UPLOAD_CALL_RECORDING_FAILED: {
      const { id, error } = action.payload as DeleteUploadCallRecordingErrorResponse;

      return {
        ...state,
        deletion: {
          ...state.deletion,
          [id]: {
            id,
            deleting: false,
            deleted: false,
            error: error,
          },
        },
      };
    }

    case INVALIDATE_UPLOAD_CALL_RECORDING: {
      return {
        ...state,
        upload: {},
        uploadedItems: {
          ...state.uploadedItems,
          items: [],
          loaded: false,
        },
      };
    }

    case FETCH_UPLOAD_CALL_MEETINGS_REQUEST: {
      return {
        ...state,
        uploadedItems: {
          ...state.uploadedItems,
          loading: true,
          loaded: false,
        },
      };
    }

    case FETCHING_UPLOAD_CALL_MEETINGS_SUCCEEDED: {
      const { response } = action.payload as FetchUploadCallMeetingsResponse;

      const existingItems = state.uploadedItems.items || [];
      let records: UploadedCallItem[] = response.records.map((item: CallMeeting) => ({
        recordingId: item.id,
        response: item,
        error: null,
      }));

      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      return {
        ...state,
        uploadedItems: {
          ...state.uploadedItems,
          loading: false,
          loaded: true,
          items: records,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1,
          },
        },
      };
    }

    case FETCHING_UPLOAD_CALL_MEETINGS_FAILED: {
      const { error } = action.payload as FetchUploadCallMeetingsErrorResponse;

      return {
        ...state,
        uploadedItems: {
          ...state.uploadedItems,
          loading: false,
          loaded: false,
          items: [],
        },
      };
    }

    default:
      return state;
  }
}